<template>
<v-dialog
  v-model="internalValue"
  max-width="820px"
  scrollable
  content-class="full-report-dialog"
>
  <v-card class="rounded-lg" style="overflow: hidden;">

    <!-- Toolbar -->
    <div class="fr-toolbar">
      <span class="fr-toolbar__title">Расширенный отчёт</span>
      <div class="d-flex align-center" style="gap: 8px;">
        <v-btn outlined small color="#009E60" class="fr-print-btn"
               :loading="pdfGenerating" :disabled="pdfGenerating"
               @click="$emit('generate-pdf')">
          <v-icon small left>mdi-file-pdf-box</v-icon>
          Скачать PDF
        </v-btn>
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <v-alert
      v-if="hasUnsavedNotes"
      type="warning" dense text tile
      class="ma-0"
    >
      Есть несохранённые выводы по компетенциям. В PDF попадёт только сохранённая версия — нажмите «Сохранить оценки» перед скачиванием.
    </v-alert>

    <!-- Report body -->
    <v-card-text id="full-report-body" class="pa-8" style="max-height: 78vh; overflow-y: auto; background: #fff;">

      <!-- Logo row -->
      <div class="fr-logo-row">
        <div class="fr-logo">
          <div class="fr-logo__avatar">
            <img src="@/assets/images/logoTA.svg" alt="">
          </div>
        </div>
        <span class="fr-logo-date">{{ date }}</span>
      </div>

      <!-- Report type label -->
      <div class="fr-type-label">Расширенный отчёт по управленческой диагностике</div>

      <!-- Participant name -->
      <h1 class="fr-name">{{ candidateName }}</h1>
      <div class="fr-sub">{{ position }} · {{ company }}</div>

      <!-- Meta row -->
      <div class="fr-meta-row">
        <div class="fr-meta-col">
          <div class="fr-meta-col__label">ЭКСПЕРТ</div>
          <div class="fr-meta-col__value">{{ manager || '—' }}</div>
        </div>
        <div class="fr-meta-col">
          <div class="fr-meta-col__label">ТИП</div>
          <div class="fr-meta-col__value">Управленческая диагностика</div>
        </div>
        <div class="fr-meta-col">
          <div class="fr-meta-col__label">ДАТА</div>
          <div class="fr-meta-col__value">{{ date }}</div>
        </div>
      </div>

      <div class="fr-divider"/>

      <!-- ── ОБЩИЙ ПРОФИЛЬ ── -->
      <h2 class="fr-section-heading">Общий профиль</h2>

      <!-- Score cards -->
      <div class="fr-score-row">
        <div class="fr-score-card">
          <div class="fr-score-card__label">ОБЩИЙ БАЛЛ</div>
          <div class="fr-score-card__value">{{ formattedScore }}</div>
        </div>
        <div class="fr-score-card">
          <div class="fr-score-card__label">ГРЕЙД</div>
          <div class="fr-score-card__value" :style="{ color: overallGradeColor }">{{ overallGradeLabel }}</div>
        </div>
        <div class="fr-score-card">
          <div class="fr-score-card__label">ЗОНА</div>
          <div class="fr-score-card__value" :style="{ color: overallGradeColor }">{{ overallZoneLabel }}</div>
        </div>
      </div>

      <template v-if="!resultSettings || resultSettings.show_result_notes">
        <div class="fr-two-col mt-4">
          <div class="fr-two-col__cell">
            <div class="fr-col-label">СИЛЬНЫЕ СТОРОНЫ</div>
            <ul v-if="strengths.length" class="fr-list">
              <li v-for="(item, i) in strengths" :key="i">{{ item }}</li>
            </ul>
            <p v-else class="fr-empty">Не указано</p>
          </div>
          <div class="fr-two-col__cell">
            <div class="fr-col-label">ЗОНЫ РОСТА</div>
            <ul v-if="growthAreas.length" class="fr-list">
              <li v-for="(item, i) in growthAreas" :key="i">{{ item }}</li>
            </ul>
            <p v-else class="fr-empty">Не указано</p>
          </div>
        </div>

        <!-- Recommendations (full-width) -->
        <div class="fr-block mt-3">
          <div class="fr-col-label">ПРИОРИТЕТЫ РАЗВИТИЯ</div>
          <ul v-if="recommendations.length" class="fr-list mt-2">
            <li v-for="(item, i) in recommendations" :key="i">{{ item }}</li>
          </ul>
          <p v-else class="fr-empty mt-2">Не указано</p>
        </div>
      </template>

      <!-- ── АНАЛИТИКА ПО КОМПЕТЕНЦИЯМ ── -->
      <template v-if="!resultSettings || resultSettings.show_graphs">
        <div class="fr-divider"/>

        <h2 class="fr-section-heading">Аналитика по компетенциям</h2>

        <!-- Bar chart -->
        <div v-if="scoredCompetencies.length" class="fr-chart-wrap">
          <DiagnosticsCompetencyChart :competency-details="competencyDetails" style="width:100%;height:100%;"/>
        </div>
      </template>

      <div
        v-for="comp in competencyDetails"
        :key="comp.id"
        class="fr-comp-card  mt-4"
      >
        <!-- Comp header -->
        <div class="fr-comp-card__header">
          <div class="fr-comp-card__meta">
            <div class="fr-comp-card__name">{{ comp.name }}</div>
            <div v-if="comp.description" class="fr-comp-card__desc">{{ comp.description }}</div>
          </div>
          <div class="fr-comp-card__score-block">
            <div class="fr-comp-card__score">{{ comp.scoreFormatted }}</div>
            <span
              v-if="comp.score !== null"
              class="fr-grade-badge"
              :style="{ background: comp.gradeBg, color: comp.gradeColor }"
            >{{ comp.gradeLabel }}</span>
          </div>
        </div>

        <!-- Indicators -->
        <div v-if="comp.indicators.length" class="fr-indicators">
          <div
            v-for="ind in comp.indicators"
            :key="ind.id"
            class="fr-indicator-row"
          >
              <span class="fr-indicator-row__score" :style="{ color: ind.scoreColor }">
                {{ ind.scoreFormatted }}
              </span>
            <span class="fr-indicator-row__name">{{ ind.name }}</span>
          </div>
        </div>

        <!-- Competency comment -->
        <div v-if="comp.comment && (!resultSettings || resultSettings.show_comments)" class="fr-comp-comment mt-3">
          <div class="fr-comp-comment__label">КОММЕНТАРИЙ</div>
          <div class="fr-comp-comment__text">{{ comp.comment }}</div>
        </div>

        <!-- Выводы ассессора по компетенции: сильные стороны / зоны роста / рекомендации -->
        <div v-if="(!resultSettings || resultSettings.show_competence_notes) && compNoteBlocks(comp).length" class="fr-comp-notes mt-3">
          <div
            v-for="block in compNoteBlocks(comp)"
            :key="block.key"
            class="fr-comp-notes__block"
            :style="{ background: block.bg, borderLeftColor: block.color }"
          >
            <div class="fr-comp-notes__label" :style="{ color: block.labelColor }">{{ block.label }}</div>
            <ul class="fr-comp-notes__list">
              <li v-for="(item, i) in block.items" :key="i">{{ item }}</li>
            </ul>
          </div>
        </div>
      </div>

      <template v-if="!resultSettings || resultSettings.show_idp">
        <div class="fr-divider"/>
        <h2 class="fr-section-heading">Индивидуальный план развития</h2>
        <div class="fr-placeholder-block">
          <span v-if="!iprRows.length" class="fr-empty">Раздел будет доступен позже</span>
          <table v-else class="fr-ipr-table">
            <thead>
            <tr>
              <th>ЗОНА</th>
              <th>ЧТО РАЗВИВАТЬ</th>
              <th>ДЕЙСТВИЯ</th>
              <th>СРОК</th>
              <th>ПРИОРИТЕТ</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(row, i) in iprRows" :key="i">
              <td>{{ row.zone }}</td>
              <td>{{ row.whatToDevelop }}</td>
              <td>{{ row.actions }}</td>
              <td>{{ row.deadline }}</td>
              <td>{{ row.priority }}</td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="fr-divider"/>
      </template>

      <!-- ── ИТОГОВЫЙ КОММЕНТАРИЙ ЭКСПЕРТА ── -->
      <template v-if="!resultSettings || resultSettings.show_conclusion">
        <div class="fr-col-label">ИТОГОВЫЙ КОММЕНТАРИЙ ЭКСПЕРТА</div>
        <div class="fr-block fr-conclusion mt-2" v-html="safeExpertConclusion"/>
      </template>
    </v-card-text>
  </v-card>
</v-dialog>
</template>

<script>
import DiagnosticsCompetencyChart from '@/components/Diagnostics/DiagnosticsCompetencyChart.vue';
import { getGrade } from '@/common/diagnosticGrades.js';
import { sanitizeRichHtml } from '@/common/util.js';

export default {
  name: 'FullReportPreviewModal',

  components: { DiagnosticsCompetencyChart },

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    pdfGenerating: {
      type: Boolean,
      default: false,
    },
    candidateName: {
      type: String,
      default: '',
    },
    company: {
      type: String,
      default: '',
    },
    position: {
      type: String,
      default: '',
    },
    manager: {
      type: String,
      default: '',
    },
    date: {
      type: String,
      default: '',
    },
    overallScore: {
      type: Number,
      default: null,
    },
    strengths: {
      type: Array,
      default: () => [],
    },
    growthAreas: {
      type: Array,
      default: () => [],
    },
    recommendations: {
      type: Array,
      default: () => [],
    },
    expertConclusion: {
      type: String,
      default: '',
    },
    competencyDetails: {
      type: Array,
      default: () => [],
    },
    // Future slots
    iprRows: {
      type: Array,
      default: () => [],
    },
    resultSettings: { type: Object, default: null },
    // Заметки по компетенциям изменены, но ещё не сохранены (в PDF их не будет)
    hasUnsavedNotes: { type: Boolean, default: false },
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },

    formattedScore() {
      return this.overallScore !== null ? Number(this.overallScore)
        .toFixed(2) : '—';
    },

    safeExpertConclusion() {
      return sanitizeRichHtml(this.expertConclusion) || '—';
    },

    overallGradeLabel() {
      return getGrade(this.overallScore)?.label || '—';
    },

    overallZoneLabel() {
      const g = getGrade(this.overallScore);
      return g ? `${g.label}` : '—';
    },

    overallGradeColor() {
      return getGrade(this.overallScore)?.color || '#9e9e9e';
    },

    scoredCompetencies() {
      return this.competencyDetails.filter(c => c.score !== null && c.score !== undefined);
    },
  },

  methods: {
    close() {
      this.$emit('input', false);
    },

    // Блоки выводов по компетенции: только непустые, в порядке из отчёта; цвета — как в редакторе диагностики.
    compNoteBlocks(comp) {
      const blocks = [
        {
          key: 'strengths', label: 'СИЛЬНЫЕ СТОРОНЫ', items: comp.strengths, color: '#009E60', labelColor: '#009E60', bg: '#F0FFF8',
        },
        {
          key: 'growthAreas', label: 'ЗОНЫ РОСТА', items: comp.growthAreas, color: '#F9A825', labelColor: '#B7791F', bg: '#FFFBF0',
        },
        {
          key: 'recommendations', label: 'РЕКОМЕНДАЦИИ', items: comp.recommendations, color: '#483CB9', labelColor: '#483CB9', bg: '#F0EEFF',
        },
      ];
      return blocks
        .map(block => ({ ...block, items: (block.items || []).filter(item => item && String(item).trim()) }))
        .filter(block => block.items.length);
    },

  },
};
</script>

<style lang="scss">
.full-report-dialog {
  max-height: 92vh;
}
</style>

<style scoped lang="scss">
.fr-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e0e0e0;

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #212121;
  }
}

.fr-print-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  border-radius: 7px !important;
}

.fr-logo-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.fr-logo {
  display: flex;
  align-items: center;
  gap: 10px;

  &__avatar {
    border-radius: 8px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 16px;
    flex-shrink: 0;
  }

  &__name {
    font-size: 15px;
    font-weight: 700;
    color: #212121;
  }
}

.fr-logo-date {
  font-size: 13px;
  color: #9e9e9e;
}

.fr-type-label {
  color: #009E60;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.fr-name {
  font-size: 28px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 4px;
  line-height: 1.2;
}

.fr-sub {
  font-size: 14px;
  color: #757575;
  margin-bottom: 20px;
}

.fr-meta-row {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.fr-meta-col {
  &__label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .07em;
    color: #9e9e9e;
    margin-bottom: 4px;
  }

  &__value {
    font-size: 13px;
    font-weight: 600;
    color: #212121;
  }
}

.fr-divider {
  height: 1px;
  background: #eeeeee;
  margin: 24px 0;
}

.fr-section-heading {
  font-size: 17px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 16px;
}

.fr-score-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.fr-score-card {
  flex: 1;
  min-width: 120px;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fff;

  &__label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .07em;
    color: #9e9e9e;
    margin-bottom: 6px;
  }

  &__value {
    font-size: 20px;
    font-weight: 700;
    color: #212121;
    line-height: 1.2;
  }
}

.fr-two-col {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  &__cell {
    flex: 1;
    min-width: 180px;
    border: 1px solid #eeeeee;
    border-radius: 8px;
    padding: 14px 16px;
    background: #fafafa;
  }
}

.fr-col-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .07em;
  color: #9e9e9e;
  margin-bottom: 8px;
}

.fr-block {
  border: 1px solid #eeeeee;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fafafa;
}

.fr-list {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: #424242;
  line-height: 1.8;
}

/* Competency cards */
.fr-comp-card {
  border: 1px solid #eeeeee;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 12px;
  background: #fff;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
  }

  &__meta {
    flex: 1;
  }

  &__name {
    font-size: 15px;
    font-weight: 700;
    color: #212121;
    margin-bottom: 4px;
  }

  &__desc {
    font-size: 12px;
    color: #757575;
    line-height: 1.5;
  }

  &__score-block {
    text-align: right;
    flex-shrink: 0;
  }

  &__score {
    font-size: 22px;
    font-weight: 700;
    color: #212121;
    line-height: 1.1;
    margin-bottom: 4px;
  }
}

.fr-grade-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.fr-indicators {
  border-top: 1px solid #f0f0f0;
  padding-top: 10px;
}

.fr-indicator-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13px;
  color: #424242;

  &:last-child {
    border-bottom: none;
  }

  &__score {
    font-weight: 700;
    font-size: 14px;
    min-width: 20px;
    flex-shrink: 0;
  }

  &__name {
    flex: 1;
    line-height: 1.4;
  }
}

.fr-comp-notes {
  &__list li {
    white-space: pre-line;
  }

  &__block {
    margin-top: 8px;
    padding: 8px 14px;
    border-left: 3px solid #bdbdbd;
    border-radius: 6px;

    &:first-child {
      margin-top: 0;
    }
  }

  &__label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .07em;
    margin-bottom: 4px;
  }

  &__list {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    color: #424242;
    line-height: 1.6;
  }
}

.fr-comp-comment {
  background: #f9f9f9;
  border-radius: 6px;
  padding: 10px 14px;

  &__label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .07em;
    color: #9e9e9e;
    margin-bottom: 4px;
  }

  &__text {
    font-size: 13px;
    color: #424242;
    line-height: 1.6;
    white-space: pre-wrap;
  }
}

.fr-placeholder-block {
  border: 1.5px dashed #e0e0e0;
  border-radius: 8px;
  padding: 18px;
  background: #fafafa;
}

.fr-ipr-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: #9e9e9e;
    letter-spacing: .05em;
    padding: 8px 12px;
    border-bottom: 1.5px solid #eeeeee;
    background: #fafafa;
    white-space: nowrap;
  }

  td {
    padding: 8px 12px;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
      border-bottom: none;
    }
  }
}

.fr-conclusion {
  font-size: 13px;
  color: #424242;
  line-height: 1.6;

  p {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.fr-empty {
  font-size: 13px;
  color: #9e9e9e;
  margin: 0;
}

.fr-chart-wrap {
  height: 240px;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  padding: 16px 16px 8px;
  background: #fff;
}

.mt-2 {
  margin-top: 8px;
}

.mt-3 {
  margin-top: 12px;
}

.mt-4 {
  margin-top: 16px;
}
</style>
